import {Button, Stack} from '@mui/material';
import {useModalDatePicker} from 'presentation/hooks/useModalDatePicker';
import Icon from 'presentation/icons';
import {translations} from 'presentation/translations-new';
import {formatShortDateString} from 'presentation/utils/formatShortDateString';
import React, {Fragment} from 'react';
import DatePicker from '../DatePicker';
import {HOVER_TYPE_ENUM} from '../DatePicker/DatePicker.types';
import {Modal} from '../Modal';

import {useSelectedBookingDateStore} from './store';
import {ModalDatePickerProps} from './types';
import {useResolvedBookingDate} from './useResolvedBookingDate';

const {confirm, clear, selectDate, changeDate} = translations['pt-br'].commons;

function ModalDatePicker({
	customLabelConfirmButton,
	showClearButton,
	customLabelClearButton,
	customOpenModalButton,
	availableDates,
	currentFirstDate,
	currentSecondDate,
	initialCalendarDate,
	disableConfirmButton,
	externalOpen,
	includeDateIntervals,
	hideOpenButton,
	selectFirstDate,
	selectSecondDate,
	selectDates,
	onAbandonModal,
	onClearDates,
	isSecondDateOptional,
	adjustDateInterval,
	showPrevMonthDay = true,
	showNextMonthDay = true,
	onConfirmDate,
	shouldHaveDateIntervals,
	dataTagId,
}: ModalDatePickerProps) {
	const {
		firstDate,
		setFirstDate,
		secondDate,
		setSecondDate,
		handleChangeDate,
		handleClearDate,
		handleCloseDatePicker,
		handleConfirmDate,
		handleOpenDatePicker,
		openDatePickerState,
		alternateSecondaryDateState,
		disableConfirmButtonState,
	} = useModalDatePicker({
		currentFirstDate,
		currentSecondDate,
		selectFirstDate,
		selectSecondDate,
		selectDates,
		onAbandonModal,
		isSecondDateOptional,
		adjustDateInterval,
	});

	const selectedBookingDate = useResolvedBookingDate();
	const resetSelectedBookingDate = useSelectedBookingDateStore(
		state => state.resetSelectedBookingDate,
	);

	const selectedDate = formatShortDateString({firstDate, secondDate, separator: 'à'});
	const fallbackText = customOpenModalButton ?? changeDate;

	let buttonText = fallbackText;

	if (selectedBookingDate) {
		buttonText = selectedDate === selectedBookingDate ? selectedDate : selectedBookingDate;
	}

	const openModal = externalOpen || openDatePickerState;

	return (
		<Fragment>
			{hideOpenButton ? null : (
				<Button
					variant="text"
					onClick={handleOpenDatePicker}
					endIcon={<Icon name="edit" size={20} />}
					data-tag-id={dataTagId?.openButton}>
					{buttonText}
				</Button>
			)}
			<Modal.Root
				open={openModal}
				onClose={handleCloseDatePicker}
				customMaxHeight={'calc(100% - 12px)'}
				fullScreen={false}>
				<Modal.Header
					onClose={handleCloseDatePicker}
					title={selectDate}
					closeBtnDataTagId={dataTagId?.closeButton}
				/>
				<Stack justifyContent={'center'} alignItems={'center'}>
					<DatePicker
						visible={openModal}
						typeTextWeekDay="short"
						hoverType={HOVER_TYPE_ENUM.CIRCLE}
						onCloseDatePicker={handleCloseDatePicker}
						handleChangeDate={handleChangeDate}
						firstDaySelected={firstDate}
						secondDaySelected={secondDate}
						initialCalendarDate={initialCalendarDate}
						onSelectFirstDay={setFirstDate}
						onSelectSecondDay={setSecondDate}
						enableSecondDaySelection={!!selectSecondDate}
						showBackgroundIntervalWhenHover={!!selectSecondDate}
						availableDates={availableDates}
						includeDateIntervals={
							shouldHaveDateIntervals
								? {
										start: includeDateIntervals?.start || new Date(1900, 1, 1),
										end: adjustDateInterval
											? alternateSecondaryDateState
											: (includeDateIntervals?.end ?? new Date()),
									}
								: undefined
						}
						showNextMonthDay={showNextMonthDay}
						showPrevMonthDay={showPrevMonthDay}
						isSingleSelection={!selectSecondDate}
					/>
				</Stack>
				<Modal.Footer>
					{showClearButton ? (
						<Button
							data-tag-id={dataTagId?.clearButton}
							variant="outlined"
							size="large"
							onClick={() => {
								handleClearDate();
								onClearDates?.();
								resetSelectedBookingDate();
							}}
							fullWidth>
							{customLabelClearButton ?? clear}
						</Button>
					) : null}
					<Button
						data-tag-id={dataTagId?.confirmButton}
						onClick={() => {
							handleConfirmDate();
							onConfirmDate?.();
							resetSelectedBookingDate();
						}}
						fullWidth
						size="large"
						disabled={disableConfirmButton || disableConfirmButtonState}>
						{customLabelConfirmButton ?? confirm}
					</Button>
				</Modal.Footer>
			</Modal.Root>
		</Fragment>
	);
}

export default ModalDatePicker;
